import { useEffect, useMemo, useState, Fragment } from "react";
import API from "../api/axios";
import { getUser } from "../auth/auth";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  LinearProgress,
  Avatar,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import { ACCENT, NAVY } from "../theme";
import RefreshButton from "../components/ui/RefreshButton";
import logo from "../assets/logo.jpg";

const STATUS = [
  { key: "SHORTLISTED", label: "Shortlisted", color: "success" },
  { key: "SELECTED", label: "Selected", color: "info" },
  { key: "L1_ROUND", label: "L1 Round", color: "info" },
  { key: "L2_ROUND", label: "L2 Round", color: "primary" },
  { key: "ON_HOLD", label: "On Hold", color: "warning" },
  { key: "REJECTED", label: "Rejected", color: "error" },
  { key: "PENDING", label: "Pending", color: "default" },
];
const statusOf = (k) => STATUS.find((s) => s.key === k) || STATUS[6];

const POSITION_STATUS_LABEL = {
  ACTIVE: "Open",
  ON_HOLD: "On Hold",
  CLOSED: "Closed",
};
const posStatusLabel = (k) => POSITION_STATUS_LABEL[k] || "Open";

function candidateName(c) {
  const fn = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return fn || c.candidateName || "Unnamed";
}

// Timezone-safe date display (dd/mm/yyyy), no new Date() shift
const fmtDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

export default function ClientDashboard() {
  const user = getUser() || {};
  const today = new Date().toISOString().split("T")[0];
  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPos, setOpenPos] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const loadData = (withSpinner = false) => {
    if (withSpinner) setLoading(true);
    Promise.all([
      API.get("/client/positions").then((r) => setJobs(r.data)),
      API.get("/client/candidates").then((r) => setCands(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  // Take the YYYY-MM-DD prefix VERBATIM (no new Date(), so no timezone shift)
  const localDateStr = (value) => {
    if (!value) return null;
    const s = String(value);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const inRange = (value) => {
    if (!from && !to) return true;
    const ds = localDateStr(value);
    if (!ds) return false;
    if (from && ds < from) return false;
    if (to && ds > to) return false;
    return true;
  };

  // Positions filtered by positionDate; candidates by candidateDate
  const filteredJobs = useMemo(
    () => jobs.filter((j) => inRange(j.positionDate || j.createdAt)),
    [jobs, from, to],
  );
  const filteredCands = useMemo(
    () => cands.filter((c) => inRange(c.candidateDate || c.createdAt)),
    [cands, from, to],
  );

  const candsByJob = useMemo(() => {
    const m = {};
    filteredCands.forEach((c) => {
      if (!c.jobCode) return;
      (m[c.jobCode] = m[c.jobCode] || []).push(c);
    });
    return m;
  }, [filteredCands]);

  // Position status counts
  const posCounts = useMemo(() => {
    const c = { OPEN: 0, ON_HOLD: 0, CLOSED: 0 };
    filteredJobs.forEach((j) => {
      const s = j.status || "ACTIVE";
      if (s === "ON_HOLD") c.ON_HOLD++;
      else if (s === "CLOSED") c.CLOSED++;
      else c.OPEN++;
    });
    return c;
  }, [filteredJobs]);

  const tot = filteredCands.length;
  const cf = (k) => filteredCands.filter((c) => c.feedback === k).length;
  const pct = (v) => (tot > 0 ? Math.round((v / tot) * 100) : 0);

  const positionKpis = [
    {
      label: "Total Positions",
      value: filteredJobs.length,
      Icon: WorkOutlineIcon,
      color: ACCENT,
      tag: "All",
    },
    {
      label: "Open Positions",
      value: posCounts.OPEN,
      Icon: LockOpenIcon,
      color: "#15803d",
      tag: "Open",
    },
    {
      label: "On Hold Positions",
      value: posCounts.ON_HOLD,
      Icon: PauseCircleIcon,
      color: "#b45309",
      tag: "Hold",
    },
    {
      label: "Closed Positions",
      value: posCounts.CLOSED,
      Icon: LockIcon,
      color: "#64748b",
      tag: "Closed",
    },
  ];

  const candidateKpis = [
    {
      label: "Total Profiles",
      value: tot,
      Icon: PeopleIcon,
      color: "#475569",
      tag: "All",
    },
    {
      label: "Pending",
      value: cf("PENDING"),
      Icon: AccessTimeIcon,
      color: "#7c3aed",
      tag: pct(cf("PENDING")) + "%",
    },
    {
      label: "Shortlisted",
      value: cf("SHORTLISTED"),
      Icon: CheckCircleIcon,
      color: "#15803d",
      tag: pct(cf("SHORTLISTED")) + "%",
    },
    {
      label: "L1 Round",
      value: cf("L1_ROUND"),
      Icon: RadioButtonCheckedIcon,
      color: "#0d9488",
      tag: pct(cf("L1_ROUND")) + "%",
    },
    {
      label: "L2 Round",
      value: cf("L2_ROUND"),
      Icon: RadioButtonCheckedIcon,
      color: "#4f46e5",
      tag: pct(cf("L2_ROUND")) + "%",
    },
    {
      label: "On Hold",
      value: cf("ON_HOLD"),
      Icon: PauseCircleIcon,
      color: "#b45309",
      tag: pct(cf("ON_HOLD")) + "%",
    },
    {
      label: "Selected",
      value: cf("SELECTED"),
      Icon: StarIcon,
      color: "#1d4ed8",
      tag: pct(cf("SELECTED")) + "%",
    },
    {
      label: "Rejected",
      value: cf("REJECTED"),
      Icon: CancelIcon,
      color: "#b91c1c",
      tag: pct(cf("REJECTED")) + "%",
    },
  ];

  const recruitersFor = (jobCode) => [
    ...new Set(
      (candsByJob[jobCode] || []).map((c) => c.recruiterName).filter(Boolean),
    ),
  ];

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Loading your dashboard…
        </Typography>
      </Box>
    );
  }

  const KpiCard = ({ k }) => (
    <Grid item xs={6} sm={4} md={3}>
      <Card sx={{ height: "100%" }}>
        <CardContent
          sx={{
            p: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: `${k.color}14`,
                color: k.color,
              }}
            >
              <k.Icon fontSize="small" />
            </Box>
            <Chip
              size="small"
              label={k.tag}
              sx={{ bgcolor: `${k.color}14`, color: k.color, fontWeight: 700 }}
            />
          </Stack>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 800,
              mt: 1.5,
              lineHeight: 1,
            }}
          >
            {k.value}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              mt: 0.5,
              fontWeight: 600,
            }}
          >
            {k.label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, pl: { xs: 7, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={logo}
              alt="Logo"
              variant="rounded"
              sx={{
                width: 46,
                height: 46,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
              }}
            />
            <Box>
              <Typography variant="h5">Client Portal</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                {user.name}
                {user.company ? ` · ${user.company}` : ""}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              type="date"
              label="From"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              sx={{ width: 150 }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              sx={{ width: 150 }}
            />
            <Button
              size="small"
              variant={
                from === today && to === today ? "contained" : "outlined"
              }
              sx={
                from === today && to === today
                  ? {
                      bgcolor: NAVY,
                      color: "#fff",
                      "&:hover": { bgcolor: NAVY },
                    }
                  : {}
              }
              onClick={() => {
                setFrom(today);
                setTo(today);
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              variant={!from && !to ? "contained" : "outlined"}
              sx={
                !from && !to
                  ? {
                      bgcolor: NAVY,
                      color: "#fff",
                      "&:hover": { bgcolor: NAVY },
                    }
                  : {}
              }
              onClick={() => {
                setFrom("");
                setTo("");
              }}
            >
              All Time
            </Button>
            <RefreshButton onClick={() => loadData(true)} />
          </Stack>
        </Stack>
      </Paper>

      {/* POSITIONS cards */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Positions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {positionKpis.map((k) => (
          <KpiCard key={k.label} k={k} />
        ))}
      </Grid>

      {/* CANDIDATES cards */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Candidates
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {candidateKpis.map((k) => (
          <KpiCard key={k.label} k={k} />
        ))}
      </Grid>

      {/* Positions + candidates list */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Your Positions &amp; Candidates</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Click a position to see the candidates submitted for it
          </Typography>
        </Box>
        <Chip
          label={`${filteredJobs.length} position${filteredJobs.length !== 1 ? "s" : ""}`}
          variant="outlined"
        />
      </Stack>

      {filteredJobs.length === 0 ? (
        <Paper sx={{ textAlign: "center", py: 7 }}>
          <WorkOutlineIcon sx={{ fontSize: 40, color: ACCENT, mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>No positions to show</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
            Positions you provide will appear here.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: { xs: 520, md: 700 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                  Job Code
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                  Role
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                  Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 700, fontSize: 12 }}
                >
                  Candidates
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.map((j) => {
                const list = candsByJob[j.jobCode] || [];
                const recs = recruitersFor(j.jobCode);
                const isOpen = openPos === j.jobCode;
                return (
                  <Fragment key={j.jobCode}>
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        "& > *": { borderBottom: "unset" },
                      }}
                      onClick={() => setOpenPos(isOpen ? null : j.jobCode)}
                    >
                      <TableCell>
                        <KeyboardArrowRightIcon
                          fontSize="small"
                          sx={{
                            transition: "transform .2s",
                            transform: isOpen ? "rotate(90deg)" : "none",
                            color: "text.secondary",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      >
                        {j.jobCode}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {j.roleName || "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {fmtDate(j.positionDate || j.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={posStatusLabel(j.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {list.length}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          py: 0,
                          borderBottom: isOpen ? undefined : "none",
                        }}
                        colSpan={6}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              m: 1,
                              ml: { xs: 1, md: 5 },
                              p: 2,
                              bgcolor: "background.default",
                              borderRadius: 1.5,
                            }}
                          >
                            <Typography
                              sx={{ fontWeight: 700, fontSize: 13, mb: 1.5 }}
                            >
                              {list.length} candidate
                              {list.length !== 1 ? "s" : ""} from {recs.length}{" "}
                              recruiter{recs.length !== 1 ? "s" : ""}
                            </Typography>
                            {list.length === 0 ? (
                              <Typography
                                sx={{ color: "text.secondary", fontSize: 13 }}
                              >
                                No candidates submitted for this position yet.
                              </Typography>
                            ) : (
                              <Box sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 640 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Candidate</TableCell>
                                      <TableCell>Recruiter</TableCell>
                                      <TableCell>Phone</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Current Company</TableCell>
                                      <TableCell>Experience</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {list.map((c) => {
                                      const st = statusOf(c.feedback);
                                      return (
                                        <TableRow key={c.id}>
                                          <TableCell sx={{ fontWeight: 600 }}>
                                            {candidateName(c)}
                                          </TableCell>
                                          <TableCell>
                                            {c.recruiterName || "—"}
                                          </TableCell>
                                          <TableCell>
                                            {c.phone || "—"}
                                          </TableCell>
                                          <TableCell>
                                            {c.email || "—"}
                                          </TableCell>
                                          <TableCell>
                                            {c.companyName || "—"}
                                          </TableCell>
                                          <TableCell>
                                            {c.totalExperience ||
                                              c.experienceYears ||
                                              "—"}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={st.label}
                                              color={st.color}
                                              variant="outlined"
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
